import type { MetaArgs } from "react-router";

import { APP_DESCRIPTION, APP_DOMAIN, APP_TITLE } from "~/constants/common";
import { PAGE_DESCRIPTIONS } from "~/constants/pageDescriptions";
import { NOINDEX_ROBOTS_DIRECTIVE } from "~/constants/robots";
import type { ICategorySeoData, IEventSeoData } from "~/types/seo";
import { getRuntimeEnv } from "~/utils/getRuntimeEnv";
import { getSiteOrigin } from "~/utils/getSiteOrigin";
import { resolveOgImage } from "~/utils/resolveOgImage";

type SeoData = ICategorySeoData | IEventSeoData;

type GenerateMetaArgs = Pick<
  MetaArgs<unknown, Record<string, unknown>>,
  "location" | "params" | "error"
>;

interface IGenerateMetaDataProps {
  seoData?: SeoData;
  pageName?: string;
  metaArgs?: GenerateMetaArgs;
  robotsDirective?: string;
  ogType?: string;
  ogSiteName?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown>[];
  categorySlug?: string | null;
  pageUrl?: string | null;
  ogImage?: string | null;
}

interface IBuildOgTagsProps {
  title: string;
  description: string;
  url?: string;
  image?: string;
  type?: string;
  siteName?: string;
}

interface IBuildTwitterTagsProps {
  card: string;
  title: string;
  description: string;
  image?: string;
}

const DEFAULT_OG_IMAGE = `${APP_DOMAIN ?? ""}/og-image.png`;

const buildRobotsMeta = (robotsDirective?: string) => {
  const runtimeEnv = getRuntimeEnv();
  if (runtimeEnv?.VITE_ENVIRONMENT !== "production") {
    return [{ name: "robots", content: NOINDEX_ROBOTS_DIRECTIVE }];
  }

  return robotsDirective ? [{ name: "robots", content: robotsDirective }] : [];
};

const buildCanonicalLink = (canonicalUrl?: string) =>
  canonicalUrl
    ? [
        {
          tagName: "link",
          rel: "canonical",
          href: canonicalUrl,
        },
      ]
    : [];

const buildOgTags = ({
  title,
  description,
  url,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  siteName,
}: IBuildOgTagsProps) => [
  { property: "og:title", content: title },
  { property: "og:description", content: description },
  { property: "og:type", content: type },
  ...(siteName ? [{ property: "og:site_name", content: siteName }] : []),
  ...(url ? [{ property: "og:url", content: url }] : []),
  ...(image ? [{ property: "og:image", content: image }] : []),
];

const buildTwitterTags = ({
  card,
  title,
  description,
  image,
}: IBuildTwitterTagsProps) => [
  { name: "twitter:card", content: card },
  { name: "twitter:title", content: title },
  { name: "twitter:description", content: description },
  ...(image
    ? [
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: title },
      ]
    : []),
];

const buildJsonLdTags = (jsonLd?: Record<string, unknown>[]) =>
  jsonLd?.length ? jsonLd.map((entry) => ({ "script:ld+json": entry })) : [];

const asEventSeoData = (seoData?: SeoData): IEventSeoData | undefined =>
  seoData && ("canonicalUrl" in seoData || "robotsDirective" in seoData)
    ? (seoData as IEventSeoData)
    : undefined;

const resolveRobotsDirective = (
  eventSeo: IEventSeoData | undefined,
  fallback?: string
) => {
  if (eventSeo?.robotsDirective) {
    return eventSeo.robotsDirective;
  }

  if (eventSeo?.isIndexable === false) {
    return NOINDEX_ROBOTS_DIRECTIVE;
  }

  return fallback;
};

export const generateMetaData = ({
  seoData,
  pageName,
  metaArgs,
  robotsDirective,
  ogType,
  ogSiteName,
  twitterCard,
  jsonLd,
  categorySlug,
  pageUrl,
  ogImage: explicitOgImage,
}: IGenerateMetaDataProps = {}) => {
  const eventSeo = asEventSeoData(seoData);
  const fallbackCanonicalUrl = `${APP_DOMAIN}${metaArgs?.location?.pathname}`;
  const canonicalUrl = eventSeo?.canonicalUrl || fallbackCanonicalUrl;
  const effectiveRobotsDirective = resolveRobotsDirective(
    eventSeo,
    robotsDirective
  );

  const canonicalLink = buildCanonicalLink(canonicalUrl);
  const robotsMeta = buildRobotsMeta(effectiveRobotsDirective);
  const jsonLdTags = buildJsonLdTags(jsonLd);

  const resolveTitle = () => {
    if (seoData) {
      return seoData.seoTitle
        ? `${seoData.seoTitle} | PredictionHero`
        : APP_TITLE;
    }

    if (pageName) {
      return `${pageName} | Prediction Markets | Odds & Market Signals – PredictionHero`;
    }

    return APP_TITLE;
  };

  const title = resolveTitle();
  const pageDescription = pageName ? PAGE_DESCRIPTIONS[pageName] : undefined;
  const description =
    seoData?.seoDescription || pageDescription || APP_DESCRIPTION;
  const ogTitle = seoData?.ogTitle || title;
  const ogDescription = seoData?.ogDescription || description;
  const ogUrl = eventSeo?.ogUrl || canonicalUrl;
  const siteOrigin = getSiteOrigin(
    eventSeo?.canonicalUrl || eventSeo?.ogUrl || pageUrl || canonicalUrl
  );
  const ogImage =
    explicitOgImage ??
    resolveOgImage(eventSeo?.ogImage, categorySlug, siteOrigin) ??
    `${siteOrigin}/og-image.png`;

  const twitterTags = twitterCard
    ? buildTwitterTags({
        card: twitterCard,
        title: ogTitle,
        description: ogDescription,
        image: ogImage,
      })
    : [];

  return [
    { title },
    { name: "description", content: description },
    ...buildOgTags({
      title: ogTitle,
      description: ogDescription,
      url: ogUrl,
      image: ogImage,
      type: ogType,
      siteName: ogSiteName,
    }),
    ...twitterTags,
    ...robotsMeta,
    ...canonicalLink,
    ...jsonLdTags,
  ];
};
